import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Coins, Rocket } from "lucide-react";
import { useWalletUser, useApp, ConnectPrompt } from "@/App";
import { crashAction } from "@/api";

export default function CrashPage() {
  const { isConnected, user, address, refresh } = useWalletUser();
  const { showToast } = useApp();
  const [wager, setWager] = useState(50);
  const [session, setSession] = useState(null);
  const [mult, setMult] = useState(1);
  const [status, setStatus] = useState("idle"); // idle | flying | cashed | crashed
  const [history, setHistory] = useState([]);
  const rafRef = useRef(null);
  const startRef = useRef(0);

  const stopLoop = () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); rafRef.current = null; };

  const loop = (crashAt) => {
    const tick = () => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      const m = +(1 + elapsed * 0.5).toFixed(2);
      if (m >= crashAt) {
        setMult(crashAt);
        setStatus("crashed");
        setHistory((h) => [{ at: crashAt, win: false }, ...h].slice(0, 10));
        showToast(`💥 Crashed at ${crashAt}x`, "error");
        refresh();
        stopLoop();
        return;
      }
      setMult(m);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const launch = async () => {
    try {
      const r = await crashAction({ address, wager: Number(wager), action: "launch" });
      setSession(r);
      setMult(1);
      setStatus("flying");
      startRef.current = Date.now();
      loop(r.crash_at);
      refresh();
    } catch (e) { showToast(e?.response?.data?.detail || "Launch failed", "error"); }
  };

  const cashout = async () => {
    stopLoop();
    try {
      const r = await crashAction({ address, wager: Number(wager), action: "cashout", session_id: session.session_id });
      if (r.status === "cashed") {
        setMult(r.cashout_mult);
        setStatus("cashed");
        setHistory((h) => [{ at: r.cashout_mult, win: true }, ...h].slice(0, 10));
        showToast(`💰 Cashed out at ${r.cashout_mult}x • +${r.flame_won} FLAME`);
      } else {
        setStatus("crashed");
        showToast(`💥 Crashed at ${r.crash_at}x`, "error");
      }
      refresh();
    } catch (e) { showToast(e?.response?.data?.detail || "Cashout failed", "error"); }
  };

  useEffect(() => () => stopLoop(), []);

  if (!isConnected) return <ConnectPrompt />;

  const flying = status === "flying";
  const bottom = Math.min(260, (mult - 1) * 40);

  return (
    <div className="pt-24 pb-20">
      <div className="max-w-5xl mx-auto px-4">
        <h1 className="font-display text-4xl font-black">Rocket Crash</h1>
        <p className="text-slate-400 mt-1">Credits: <span className="text-cyan-400 font-mono">{user?.credits ?? 0}</span> • FLAME: <span className="text-orange-400 font-mono">{user?.flame ?? 0}</span></p>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="md:col-span-2 rocket-stage" data-testid="game-crash-rocket-canvas">
            <motion.div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_80%,rgba(255,85,0,0.2),transparent_60%)]" />
            <div className="rocket" style={{ bottom: `${bottom}px` }}>
              <motion.div animate={{ y: flying ? [-2, 2, -2] : 0 }} transition={{ repeat: Infinity, duration: 0.3 }}>🚀</motion.div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div data-testid="game-crash-multiplier-text" className={`font-display text-7xl font-black ${status === "crashed" ? "text-rose-500" : status === "cashed" ? "text-emerald-400" : "text-white"}`}>
                {mult.toFixed(2)}x
              </div>
            </div>
            {status === "crashed" && <div className="absolute inset-0 flex items-center justify-center text-5xl">💥</div>}
          </div>

          <div className="card-tile card-flame">
            <h3 className="font-display text-xl font-bold">Control panel</h3>
            <div className="mt-3 flex items-center gap-2">
              <Coins size={16} className="text-cyan-400" />
              <input data-testid="crash-wager-input" type="number" min="10" step="10" value={wager}
                onChange={(e) => setWager(Number(e.target.value))} disabled={flying}
                className="bg-[#12131C] border border-[#2A2E45] rounded-xl px-4 py-2 w-32 font-mono disabled:opacity-50" />
            </div>
            {!flying ? (
              <button data-testid="game-crash-launch-button" onClick={launch} disabled={!user || user.credits < wager} className="btn-tactile btn-flame w-full mt-4 font-display text-lg">
                <Rocket size={18} className="inline mr-2" /> LAUNCH
              </button>
            ) : (
              <button data-testid="game-crash-cashout-button" onClick={cashout} className="btn-tactile btn-gold w-full mt-4 font-display text-lg">
                CASH OUT ({Math.floor(wager * mult)} FLAME)
              </button>
            )}
            <div className="mt-5">
              <div className="font-pixel text-[9px] text-slate-400">RECENT ROUNDS</div>
              <div className="flex gap-1 flex-wrap mt-2">
                {history.map((h, i) => (
                  <span key={i} className={`text-xs font-mono px-2 py-1 rounded ${h.win ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>{h.at}x</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
